import React, { useState } from 'react';
import { startNoCodeGoogleLogin } from '../lib/authWorker';
import { 
  X, 
  Sparkles, 
  CheckCircle2, 
} from 'lucide-react';

interface LazyAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextAction?: string; // 'buy_volume' | 'take_exam' | 'telegram' | 'navbar' | string
  postTitle?: string;
}

export const LazyAuthModal: React.FC<LazyAuthModalProps> = ({
  isOpen,
  onClose,
  contextAction = 'navbar',
  postTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  const getDomainName = () => {
    const params = new URLSearchParams(window.location.search);
    let domainName = params.get('subdomain') || params.get('custom_domain');
    if (!domainName) {
      let currentHost = window.location.hostname;
      if (currentHost.endsWith('.mecopay.space')) {
         domainName = currentHost.replace('.mecopay.space', '');
      } else {
         domainName = currentHost;
      }
    }
    return domainName ? domainName.charAt(0).toUpperCase() + domainName.slice(1) : 'Our Platform';
  };

  const getContextDescription = () => {
    const brandName = getDomainName();
    switch (contextAction) {
      case 'buy_volume':
        return {
          title: 'Sign In to Unlock Volume',
          subtitle: postTitle 
            ? `Sign in with Google to purchase and download "${postTitle}" with lifetime PDF updates.`
            : 'Sign in with Google to purchase official dreamers volumes and get access to high-yield notes.',
          badge: 'Exclusive Admission Volume',
        };
      case 'take_exam':
        return {
          title: 'Sign In to Take Live Exam',
          subtitle: postTitle
            ? `Sign in with Google to start "${postTitle}" with real-time negative marking and merit list rank.`
            : 'Sign in to record your test score and compare your standing on the national leaderboard.',
          badge: 'Model Test Simulator',
        };
      case 'telegram':
        return {
          title: 'Sign In for Telegram Invite',
          subtitle: 'Sign in with Google to generate your verified student invitation link to the private Telegram discussion channel.',
          badge: 'Verified Community',
        };
      default:
        return {
          title: `Join ${brandName}`,
          subtitle: 'Sign in with Google to save your study progress, access model tests, and download admission materials.',
          badge: 'Student Portal',
        };
    }
  };

  const contextInfo = getContextDescription();

  // Start the tenant OAuth broker with a top-level navigation. The Gateway
  // validates this no-code host and forwards the request to the Auth Worker.
  const handleGoogleSignIn = () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      startNoCodeGoogleLogin();
    } catch (err: any) {
      console.error('Google OAuth error:', err);
      setErrorMsg(err.message || 'Could not start Google sign-in. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Accent Gradient */}
        <div className="h-2 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-7">
          {/* Badge & Title */}
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
              <Sparkles className="w-3.5 h-3.5" />
              {contextInfo.badge}
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-2">
            {contextInfo.title}
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mb-6 leading-relaxed">
            {contextInfo.subtitle}
          </p>

          {/* Benefits Grid */}
          <div className="bg-slate-950/60 rounded-xl p-3.5 border border-slate-800/80 mb-6 space-y-2 text-xs text-slate-300">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Instant sync across mobile & desktop devices</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Negative marking exam analytics & national rank</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Exclusive 15-year BUET & DMC solved archives</span>
            </div>
          </div>

          {errorMsg && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300">
              {errorMsg}
            </div>
          )}

          {/* Primary Action: Google OAuth */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl bg-white hover:bg-slate-100 text-slate-900 font-semibold text-sm shadow-lg shadow-white/5 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 cursor-pointer"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            <span>{loading ? 'Connecting to Google...' : 'Continue with Google'}</span>
          </button>

          <p className="mt-5 text-[11px] text-center text-slate-400">
            By signing in, you agree to {getDomainName()} Terms of Service & Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LazyAuthModal;
